import { Box, Theme, Typography, useTheme } from "@outschool/backpack";
import { useProductListViewed } from "@outschool/ui-analytics";
import { gql } from "@outschool/ui-apollo";
import { ListingCardFragment } from "@outschool/ui-components-website";
import { Carousel, Container } from "@outschool/ui-legacy-component-library";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

import { ListingCard, ListingCardLoading } from "../ListingCard";
import { ListingCarouselProps } from "./types";

function ListingElements({
  listings,
  trackingLabel,
  carouselRowNum,
  hideTeacher = false,
  showSummary = false,
  openInSSRForLoggedOutUsers,
  trackingProperties,
  isCrossSell = false,
  shareButtonFunction,
  overrideShowCredits = false,
  currentIndex,
  columnWidth
}: Pick<
  ListingCarouselProps,
  | "listings"
  | "trackingLabel"
  | "carouselRowNum"
  | "hideTeacher"
  | "showSummary"
  | "openInSSRForLoggedOutUsers"
  | "trackingProperties"
  | "shareButtonFunction"
  | "currentIndex"
> & {
  isCrossSell?: boolean;
  overrideShowCredits?: boolean;
  columnWidth?: number;
}) {
  const trackProductListViewed = useProductListViewed();

  // @ts-ignore TS(2345): Argument of type 'ListingCarousel_ListingFragment... Remove this comment to see the full error message
  trackProductListViewed("featured-class-row", trackingLabel, listings);

  if (!listings) {
    return null;
  }

  return (
    <>
      {listings.map((listing, index) => (
        <ListingCard
          key={`${trackingLabel}-${listing.activity.uid}`}
          listing={listing}
          hideTeacher={hideTeacher}
          showSummary={showSummary}
          trackingLabel={trackingLabel}
          trackingOrder={index + 1}
          carouselRowNum={carouselRowNum}
          showRating={true}
          openInSSRForLoggedOutUsers={openInSSRForLoggedOutUsers}
          pageLocation={trackingLabel}
          passSectionUid={false}
          trackingProperties={trackingProperties}
          isCrossSell={isCrossSell}
          shareButtonFunction={
            !!shareButtonFunction ? () => shareButtonFunction(index) : undefined
          }
          isCurrentlyViewing={currentIndex == index}
          overrideShowCredits={overrideShowCredits}
          columnWidth={columnWidth}
        />
      ))}
    </>
  );
}

function ListingElementsLoading({
  isTutoringCLP = false
}: {
  isTutoringCLP: boolean;
}) {
  return (
    <>
      {[...Array(8).keys()].map(el => (
        <ListingCardLoading key={el} isTutoringCLP={isTutoringCLP} />
      ))}
    </>
  );
}

export default function ListingCarousel({
  isLoading,
  title,
  titleElement = <></>,
  alwaysShown,
  sx,
  headingSx = {},
  innerContainerSx,
  outerContainerSx,
  leftAlign = false,
  isTutoringCLP = false,
  children,
  arrowSx,
  rightArrowSx,
  leftArrowSx,
  scrollWidth,
  customScrollFunction,
  containerWidthOffset,
  ...props
}: ListingCarouselProps & {
  titleElement?: JSX.Element;
  headingSx?: SystemStyleObject;
  innerContainerSx?: SystemStyleObject;
  outerContainerSx?: SystemStyleObject;
  isCrossSell?: boolean;
  leftAlign?: boolean;
  isTutoringCLP?: boolean;
  arrowSx?: SystemStyleObject;
  rightArrowSx?: SystemStyleObject;
  leftArrowSx?: SystemStyleObject;
  scrollWidth?: number;
  columnWidth?: number;
  customScrollFunction?: (
    element: HTMLElement,
    distance: number | null,
    direction: "left" | "right"
  ) => void;
  containerWidthOffset?: number;
}) {
  const theme = useTheme();

  if ((!props.listings || !props.listings.length) && !alwaysShown) {
    return null;
  }

  let titleComponent: JSX.Element | null;
  if (title) {
    titleComponent = (
      <Typography
        as={Box}
        variant="h3"
        sx={(theme: Theme) => ({
          marginTop: "0.5em",
          marginBottom: "0.5em",
          [theme.breakpoints.up("md")]: {
            marginBottom: 0
          },
          textAlign: "center",
          ...headingSx
        })}
      >
        {title}
      </Typography>
    );
  } else if (titleElement) {
    titleComponent = titleElement;
  } else {
    titleComponent = null;
  }

  const contents = (
    <Box
      sx={{
        paddingTop: "12px",
        ...outerContainerSx
      }}
    >
      {titleComponent}
      <Carousel
        sx={{
          color: sx?.color,
          backgroundColor: sx?.backgroundColor
        }}
        leftAlign={leftAlign}
        arrowSx={arrowSx}
        rightArrowSx={rightArrowSx}
        leftArrowSx={leftArrowSx}
        scrollWidth={scrollWidth}
        customScrollFunction={customScrollFunction}
        containerWidthOffset={containerWidthOffset || 200}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            [theme.breakpoints.up("md")]: {
              paddingY: "1em"
            },
            ...innerContainerSx
          })}
        >
          {isLoading ? (
            <ListingElementsLoading isTutoringCLP={isTutoringCLP} />
          ) : (
            <ListingElements {...props} />
          )}
        </Box>
      </Carousel>
      {children}
    </Box>
  );

  return (
    <>
      <Container
        sx={{
          marginX: "auto",
          paddingY: "large",
          ...sx,
          [theme.breakpoints.down("md")]: {
            display: "none"
          }
        }}
      >
        {contents}
      </Container>

      <Box
        sx={[
          (theme: Theme) => ({
            [theme.breakpoints.up("md")]: {
              display: "none"
            }
          }),
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        {contents}
      </Box>
    </>
  );
}

ListingCarousel.fragments = {
  listing: gql`
    fragment ListingCarousel_listing on Listing {
      ...ListingCard_listing
    }
    ${ListingCardFragment}
  `
};
